import { useEffect, useRef, useState } from "react";

import { useAppearance } from "@/domain/user/components/appearance-context";

interface UseRightAsideTransitionProps {
  key: string | number;
  transitionDuration?: number;
}

export const useRightAsideTransition = ({ key, transitionDuration = 300 }: UseRightAsideTransitionProps) => {
  const { animationsEnabled } = useAppearance();
  const [isVisible, setIsVisible] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const previousKeyRef = useRef<string | number | undefined>(key);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If animations are disabled, show content immediately
    if (!animationsEnabled) {
      setShouldRender(true);
      setIsVisible(true);
      setIsTransitioning(false);
      previousKeyRef.current = key;
      return;
    }

    // If the key has changed, start transition
    if (previousKeyRef.current !== key) {
      setIsTransitioning(true);
      setIsVisible(false);

      // After fade out, update content and fade in
      timeoutRef.current = setTimeout(() => {
        setShouldRender(true);
        setIsVisible(true);

        // After fade in completes, stop transitioning
        timeoutRef.current = setTimeout(() => {
          setIsTransitioning(false);
          previousKeyRef.current = key;
        }, transitionDuration);
      }, transitionDuration);
    } else {
      // Initial render or same key - show immediately
      setShouldRender(true);
      setIsVisible(true);
      setIsTransitioning(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [key, transitionDuration, animationsEnabled]);

  return {
    isVisible,
    isTransitioning,
    shouldRender,
  };
};
